import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building, 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Eye, 
  EyeOff, 
  FileText, 
  MapPin,
  Utensils,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Shield,
  Sparkles,
  Zap,
  Clock,
  Award,
  Star
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Dados da Escola
    schoolName: '',
    cnpj: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Dados do Administrador
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    adminRole: '',
    
    // Credenciais
    password: '',
    confirmPassword: '',
    
    // Termos
    acceptTerms: false,
    acceptNewsletter: false
  });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.schoolName) newErrors.schoolName = 'Nome da escola é obrigatório';
    if (!formData.cnpj) newErrors.cnpj = 'CNPJ é obrigatório';
    if (!formData.email) newErrors.email = 'E-mail da escola é obrigatório';
    if (!formData.phone) newErrors.phone = 'Telefone é obrigatório';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.adminName) newErrors.adminName = 'Nome do administrador é obrigatório';
    if (!formData.adminEmail) newErrors.adminEmail = 'E-mail do administrador é obrigatório';
    if (!formData.adminPhone) newErrors.adminPhone = 'Telefone do administrador é obrigatório';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.password) newErrors.password = 'Senha é obrigatória';
    if (formData.password.length < 8) newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    if (!formData.acceptTerms) newErrors.acceptTerms = 'Você deve aceitar os termos de uso';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let isValid = false;
    
    if (currentStep === 1) isValid = validateStep1();
    if (currentStep === 2) isValid = validateStep2();
    
    if (isValid && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) return;
    
    setLoading(true);
    
    try {
      await signUp(formData);
      navigate('/confirmation');
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatZipCode = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const stepTitles = {
    1: 'Dados da Escola',
    2: 'Administrador',
    3: 'Finalizar Cadastro'
  };

  const stepDescriptions = {
    1: 'Informações básicas da sua escola',
    2: 'Dados do responsável pelo sistema',
    3: 'Credenciais de acesso e termos'
  };

  const InputField = ({ label, type, name, icon: Icon, placeholder, value, onChange, error, maxLength, required = false, ...props }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-300">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative group">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className={`w-5 h-5 transition-colors ${
              error ? 'text-red-400' : 'text-gray-400 group-hover:text-blue-400'
            }`} />
          </div>
        )}
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 backdrop-blur-sm ${
            error 
              ? 'border-red-500/50 focus:ring-red-500/20' 
              : 'border-gray-600/50 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50'
          }`}
          {...props}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
      {error && (
        <p className="text-sm text-red-400 animate-pulse">{error}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Cursor personalizado */}
      <div 
        className="fixed w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full pointer-events-none z-50 mix-blend-difference transition-transform duration-100 ease-out"
        style={{
          left: mousePosition.x - 12,
          top: mousePosition.y - 12,
        }}
      />

      {/* Lado esquerdo - Formulário */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-y-auto">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative w-full max-w-lg space-y-8 my-8">
          {/* Header */}
          <div className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                  <Utensils className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            {/* Títulos */}
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
              Cadastrar Nova Escola
            </h1>
            <p className="text-gray-400 mb-2">
              {stepDescriptions[currentStep]}
            </p>

            {/* Badge promocional */}
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 mt-2">
              <Sparkles className="w-4 h-4 mr-2 text-green-400" />
              <span className="text-xs text-green-300">🎉 30 dias grátis + Setup incluso</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-medium text-gray-300">
                Passo {currentStep} de 3: {stepTitles[currentStep]}
              </span>
              <span className="text-sm text-gray-400">
                {Math.round((currentStep / 3) * 100)}%
              </span>
            </div>
            <div className="flex items-center">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      step <= currentStep
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                  </div>
                  {step < 3 && (
                    <div className="flex-1 h-2 mx-3 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ${
                          step < currentStep ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Formulário */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3 animate-pulse" />
                    <p className="text-sm text-red-300">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Step 1: Dados da Escola */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <InputField
                    label="Nome da Escola"
                    type="text"
                    name="schoolName"
                    icon={Building}
                    placeholder="Ex: Colégio Dom Pedro II"
                    value={formData.schoolName}
                    onChange={handleChange}
                    error={errors.schoolName}
                    required
                  />

                  <InputField
                    label="CNPJ"
                    type="text"
                    name="cnpj"
                    icon={FileText}
                    placeholder="00.000.000/0000-00"
                    value={formatCNPJ(formData.cnpj)}
                    onChange={(e) => handleChange({
                      target: { name: 'cnpj', value: e.target.value.replace(/\D/g, '') }
                    })}
                    error={errors.cnpj}
                    maxLength={18}
                    required
                  />

                  <InputField
                    label="E-mail da Escola"
                    type="email"
                    name="email"
                    icon={Mail}
                    placeholder="contato@escola.com.br"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                  />

                  <InputField
                    label="Telefone"
                    type="text"
                    name="phone"
                    icon={Phone}
                    placeholder="(11) 99999-9999"
                    value={formatPhone(formData.phone)}
                    onChange={(e) => handleChange({
                      target: { name: 'phone', value: e.target.value.replace(/\D/g, '') }
                    })}
                    error={errors.phone}
                    maxLength={15}
                    required
                  />

                  <InputField
                    label="Endereço"
                    type="text"
                    name="address"
                    icon={MapPin}
                    placeholder="Rua das Flores, 123"
                    value={formData.address}
                    onChange={handleChange}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Cidade"
                      type="text"
                      name="city"
                      placeholder="São Paulo"
                      value={formData.city}
                      onChange={handleChange}
                    />

                    <InputField
                      label="CEP"
                      type="text"
                      name="zipCode"
                      placeholder="00000-000"
                      value={formatZipCode(formData.zipCode)}
                      onChange={(e) => handleChange({
                        target: { name: 'zipCode', value: e.target.value.replace(/\D/g, '') }
                      })}
                      maxLength={9}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Dados do Administrador */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <InputField
                    label="Nome Completo do Administrador"
                    type="text"
                    name="adminName"
                    icon={User}
                    placeholder="João Silva Santos"
                    value={formData.adminName}
                    onChange={handleChange}
                    error={errors.adminName}
                    required
                  />

                  <InputField
                    label="E-mail do Administrador"
                    type="email"
                    name="adminEmail"
                    icon={Mail}
                    placeholder="joao@escola.com.br"
                    value={formData.adminEmail}
                    onChange={handleChange}
                    error={errors.adminEmail}
                    required
                  />

                  <InputField
                    label="Telefone do Administrador"
                    type="text"
                    name="adminPhone"
                    icon={Phone}
                    placeholder="(11) 99999-9999"
                    value={formatPhone(formData.adminPhone)}
                    onChange={(e) => handleChange({
                      target: { name: 'adminPhone', value: e.target.value.replace(/\D/g, '') }
                    })}
                    error={errors.adminPhone}
                    maxLength={15}
                    required
                  />

                  <InputField
                    label="Cargo/Função"
                    type="text"
                    name="adminRole"
                    placeholder="Diretor, Coordenador, etc."
                    value={formData.adminRole}
                    onChange={handleChange}
                  />
                </div>
              )}

              {/* Step 3: Credenciais e Termos */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Senha de Acesso <span className="text-red-400">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className={`w-5 h-5 transition-colors ${
                          errors.password ? 'text-red-400' : 'text-gray-400 group-hover:text-blue-400'
                        }`} />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Mínimo 8 caracteres"
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-12 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 backdrop-blur-sm ${
                          errors.password 
                            ? 'border-red-500/50 focus:ring-red-500/20' 
                            : 'border-gray-600/50 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50'
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-400 animate-pulse">{errors.password}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Confirmar Senha <span className="text-red-400">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className={`w-5 h-5 transition-colors ${
                          errors.confirmPassword ? 'text-red-400' : 'text-gray-400 group-hover:text-blue-400'
                        }`} />
                      </div>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Digite a senha novamente"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-12 py-3 bg-gray-800/50 border rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 backdrop-blur-sm ${
                          errors.confirmPassword 
                            ? 'border-red-500/50 focus:ring-red-500/20' 
                            : 'border-gray-600/50 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50'
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-400 animate-pulse">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        id="acceptTerms"
                        name="acceptTerms"
                        type="checkbox"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-500 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                      />
                      <label htmlFor="acceptTerms" className="ml-3 text-sm text-gray-300">
                        Li e aceito os{' '}
                        <Link to="/termos" className="text-blue-400 hover:text-blue-300 underline">
                          termos de uso
                        </Link>{' '}
                        e a{' '}
                        <Link to="/privacidade" className="text-blue-400 hover:text-blue-300 underline">
                          política de privacidade
                        </Link>
                        <span className="text-red-400 ml-1">*</span>
                      </label>
                    </div>
                    {errors.acceptTerms && (
                      <p className="text-sm text-red-400 animate-pulse">{errors.acceptTerms}</p>
                    )}

                    <div className="flex items-start">
                      <input
                        id="acceptNewsletter"
                        name="acceptNewsletter"
                        type="checkbox"
                        checked={formData.acceptNewsletter}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-500 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                      />
                      <label htmlFor="acceptNewsletter" className="ml-3 text-sm text-gray-300">
                        Quero receber novidades e dicas sobre gestão escolar por e-mail
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6">
                {currentStep > 1 ? (
                  <button 
                    type="button" 
                    onClick={handleBack}
                    className="group bg-gradient-to-br from-gray-700 to-gray-600 border border-gray-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:border-gray-400 flex items-center"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                    Voltar
                  </button>
                ) : (
                  <Link to="/login" className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao login
                  </Link>
                )}
                
                <div className="ml-auto">
                  {currentStep < 3 ? (
                    <button 
                      type="button" 
                      onClick={handleNext}
                      className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 flex items-center"
                    >
                      Próximo
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!formData.acceptTerms || loading}
                      className="group relative bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Criando conta...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Zap className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                          Finalizar Cadastro
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10" />
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-black text-gray-500">Já tem uma conta?</span>
              </div>
            </div>

            <Link to="/login">
              <button className="group w-full bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10">
                <div className="flex items-center justify-center">
                  <Shield className="w-5 h-5 mr-2 group-hover:text-blue-400 transition-colors" />
                  Fazer Login
                </div>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Lado direito - Benefits & Social Proof */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-black" />
        
        {/* Animated background elements */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '3s' }} />

        <div className="relative z-10 flex flex-col justify-center px-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                Junte-se a 1000+
              </span>
              <br />
              <span className="text-white">
                escolas que confiam
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              <strong className="text-white">Setup gratuito</strong> em 24h + 
              <strong className="text-green-400"> 30 dias grátis</strong> para testar 
              todos os recursos.
            </p>

            {/* Benefits List */}
            <div className="space-y-4 mb-8">
              {[
                { text: "Implementação em 24 horas", icon: Clock, color: "from-green-500 to-emerald-500" },
                { text: "Aumento médio de 60% nas vendas", icon: Zap, color: "from-blue-500 to-cyan-500" },
                { text: "Redução de 85% no tempo de fila", icon: Award, color: "from-purple-500 to-pink-500" },
                { text: "Suporte dedicado 24/7", icon: Shield, color: "from-orange-500 to-red-500" }
              ].map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index} 
                    className="flex items-center space-x-3 text-gray-300"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className={`w-10 h-10 bg-gradient-to-r ${benefit.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Social Proof Card */}
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 rounded-2xl border border-gray-600/30 backdrop-blur-sm p-6 mb-8">
              <div className="flex items-center mb-4">
                <div className="flex -space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-2 border-black flex items-center justify-center text-sm">
                      👤
                    </div>
                  ))}
                </div>
                <span className="ml-3 text-sm text-gray-300">+1000 escolas ativas</span>
              </div>
              
              <div className="flex items-center mb-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current mr-1" />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-400">4.9/5 (247 avaliações)</span>
              </div>
              
              <blockquote className="text-gray-300 italic text-sm">
                "O CantinaTech aumentou nossas vendas em 65% no primeiro mês. 
                Implementação super rápida!"
              </blockquote>
              <cite className="text-xs text-gray-400 not-italic mt-2 block">
                - Maria Silva, Diretora do Colégio Horizonte
              </cite>
            </div>

            {/* Security Badge */}
            <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-xl p-4">
              <div className="flex items-center">
                <Shield className="w-6 h-6 text-green-400 mr-3" />
                <div>
                  <h4 className="text-sm font-semibold text-white">100% Seguro</h4>
                  <p className="text-xs text-gray-400">Dados protegidos com criptografia SSL</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;